// external
import React, { useState, useEffect, useCallback, memo } from "react";
import { PropTypes } from "prop-types";
import { withTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

// internal
import ContentAdminCast from "./ContentAdminCast";
import Spinner from "../../utils/Spinner";
import MediaAPI from "../../../api/MediaAPI";

const getTypedFilteredCastings = (castings) => {
  const filtered = {};
  castings?.forEach((c) => {
    if (filtered[c.type]) {
      filtered[c.type].push(c);
    } else {
      filtered[c.type] = [c];
    }
  });
  return filtered;
};

const castsTypes = [
  {
    title: "casting",
    type: "actor",
  },
  {
    title: "directors",
    type: "director",
  },
  {
    title: "producers",
    type: "producer",
  },
  {
    title: "authors",
    type: "author",
  },
  {
    title: "sales",
    type: "sale",
  },
  {
    title: "contacts",
    type: "contact",
    separator: true,
  },
];

function ContentAdminCasts({ t, mediaCastings, isDisabled, onUpdateCasting }) {
  const [typeFilteredCastings, setTypeFilteredCastings] = useState(
    getTypedFilteredCastings(mediaCastings),
  );

  const getCastingsQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getCastings,
    params: [{ page: 1, perPage: "all" }],
  });

  const { isLoading, data: castings } = useQuery(getCastingsQuery);

  useEffect(() => {
    setTypeFilteredCastings(getTypedFilteredCastings(mediaCastings));
  }, [mediaCastings]);

  const onUpdate = useCallback((mediaCastings, type) => {
    // Update filtered casting for this type
    typeFilteredCastings[type] = mediaCastings;

    // Rebuild medias casting array with all types
    let medias_castings = [];
    Object.entries(typeFilteredCastings).forEach(
      ([, entries]) => (medias_castings = [...medias_castings, ...entries]),
    );

    // Update item media castings
    onUpdateCasting(medias_castings);

    setTypeFilteredCastings(typeFilteredCastings);
  });

  if (isLoading)
    return (
      <div className="flex items-center justify-center p-[5rem]">
        <Spinner />
      </div>
    );

  return (
    <>
      {castsTypes.map((casts, idx) => (
        <ContentAdminCast
          key={`contentAdminCast_${idx}`}
          isDisabled={isDisabled}
          title={t(casts.title)}
          type={casts.type}
          separator={casts.separator}
          onUpdateCasting={onUpdate}
          items={typeFilteredCastings[casts.type] || []}
          castings={castings}
        />
      ))}
    </>
  );
}

ContentAdminCasts.propTypes = {
  t: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  onUpdateCasting: PropTypes.func.isRequired,
  mediaCastings: PropTypes.array.isRequired,
};

export default withTranslation("editor-content")(memo(ContentAdminCasts));
